import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { http, inr, computeTotals, requestErrorMessage } from "@/lib/erp";
import { SDOC } from "@/lib/erpdocs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2, Save } from "lucide-react";
import { useItemConfig } from "@/components/ItemConfig";
import { toast } from "sonner";
import { resolveDocumentText } from "@/lib/documentSettings";

const ICOLS = [
  { key: "hsn", label: "HSN" }, { key: "batch_no", label: "Batch" },
  { key: "qty", label: "Qty" }, { key: "rate", label: "Rate" },
  { key: "discount_pct", label: "Disc%" }, { key: "gst_rate", label: "GST%" },
];

const newItem = () => ({ product_id: "", name: "", hsn: "", batch_no: "", expiry_date: "", unit: "PCS", qty: 1, free_qty: 0, rate: 0, mrp: 0, discount_pct: 0, gst_rate: 12 });
const STATES = [["36", "Telangana"], ["37", "Andhra Pradesh"], ["29", "Karnataka"], ["27", "Maharashtra"], ["33", "Tamil Nadu"], ["07", "Delhi"], ["24", "Gujarat"], ["09", "Uttar Pradesh"], ["19", "West Bengal"], ["32", "Kerala"]];
const savedBankLabel = (details) => [
  "Saved on this bill",
  details?.bank_name || details?.name,
  details?.account_number || details?.bank_account || details?.account,
  details?.ifsc || details?.bank_ifsc,
].filter(Boolean).join(" · ");

export default function SalesDocForm({ dtype }) {
  const cfg = SDOC[dtype];
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [posCode, setPosCode] = useState("36");
  const [posName, setPosName] = useState("Telangana");
  const [docDate, setDocDate] = useState(new Date().toISOString().slice(0, 10));
  const [validUntil, setValidUntil] = useState("");
  const [items, setItems] = useState([newItem()]);
  const [shipping, setShipping] = useState(0);
  const [other, setOther] = useState(0);
  const [roundOff, setRoundOff] = useState(true);
  const [transporter, setTransporter] = useState("");
  const [vehicleNo, setVehicleNo] = useState("");
  const [lrNo, setLrNo] = useState("");
  const [notes, setNotes] = useState("");
  const [company, setCompany] = useState({});
  const [document, setDocument] = useState(null);
  const [bankId, setBankId] = useState("__none__");
  const [originalBankSelection, setOriginalBankSelection] = useState(null);
  const [saving, setSaving] = useState(false);
  const [priceLists, setPriceLists] = useState([]);
  const [priceListId, setPriceListId] = useState("");
  const [plRates, setPlRates] = useState({});
  const [priceRatesLoading, setPriceRatesLoading] = useState(false);
  const [priceRatesError, setPriceRatesError] = useState("");
  const [priceRatesRetry, setPriceRatesRetry] = useState(0);
  const [prereqLoading, setPrereqLoading] = useState(true);
  const [prereqError, setPrereqError] = useState("");
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState("");
  const [documentLoading, setDocumentLoading] = useState(!!editing);
  const [documentError, setDocumentError] = useState("");
  const itc = useItemConfig(`sdoc_${dtype}`, dtype, ICOLS);

  const loadPrerequisites = async () => {
    setPrereqLoading(true); setPrereqError("");
    try {
      const [customerRes, productRes] = await Promise.all([http.get("/customers"), http.get("/products")]);
      setCustomers(customerRes.data); setProducts(productRes.data);
    } catch (e) {
      setPrereqError(requestErrorMessage(e, "load customers and products"));
    } finally { setPrereqLoading(false); }
    try { const r = await http.get("/masters/price_lists"); setPriceLists(r.data); }
    catch (e) { toast.warning(requestErrorMessage(e, "load price lists")); }
  };
  const loadSettings = async () => {
    setSettingsLoading(true); setSettingsError("");
    try {
      const { data } = await http.get("/settings");
      setCompany(data || {});
      if (!editing) {
        const banks = data?.banks || [];
        const defaultBank = banks.find((bank) => String(bank.id) === String(data?.default_bank_id));
        setBankId(defaultBank ? String(defaultBank.id) : "__none__");
      }
    } catch (e) { setSettingsError(requestErrorMessage(e, "load company settings")); }
    finally { setSettingsLoading(false); }
  };
  useEffect(() => { loadPrerequisites(); loadSettings(); }, [editing]);

  useEffect(() => {
    if (!priceListId) {
      setPlRates({}); setPriceRatesError(""); setPriceRatesLoading(false);
      return undefined;
    }
    let active = true;
    setPriceRatesLoading(true); setPriceRatesError("");
    http.get(`/price-lists/${priceListId}/items`).then((r) => {
      if (!active) return;
      const m = {}; r.data.forEach((x) => { m[x.product_id] = x.price; }); setPlRates(m);
    }).catch((e) => {
      if (active) setPriceRatesError(requestErrorMessage(e, "load selected price list rates"));
    }).finally(() => {
      if (active) setPriceRatesLoading(false);
    });
    return () => { active = false; };
  }, [priceListId, priceRatesRetry]);

  const loadDocument = async () => {
    setDocumentLoading(true); setDocumentError("");
    try {
      const { data } = await http.get(`/sales-docs/${cfg.api}/${id}`);
      setDocument(data);
      setCustomerId(data.customer_id); setPosCode(data.place_of_supply_code); setPosName(data.place_of_supply);
      setDocDate(data.doc_date); setValidUntil(data.valid_until || "");
      setItems(data.items.map((it) => ({ ...newItem(), ...it })));
      setShipping(data.shipping); setOther(data.other_charges);
      setTransporter(data.transporter || ""); setVehicleNo(data.vehicle_no || ""); setLrNo(data.lr_no || "");
      setNotes(data.notes || "");
      const savedSelection = data.bank_id != null && data.bank_id !== ""
        ? String(data.bank_id)
        : (data.bank_details ? "__saved__" : "__saved_none__");
      setBankId(savedSelection);
      setOriginalBankSelection(savedSelection);
    } catch (e) { setDocumentError(requestErrorMessage(e, `load ${cfg.label.toLowerCase()}`)); }
    finally { setDocumentLoading(false); }
  };
  useEffect(() => { if (editing) loadDocument(); }, [id, dtype]);

  useEffect(() => {
    const c = customers.find((x) => x.id === customerId);
    if (c && !editing) { setPosCode(c.state_code); setPosName(c.state); }
  }, [customerId, customers]);

  const t = useMemo(() => computeTotals(items, posCode, { shipping, other_charges: other, round_off_enabled: roundOff }), [items, posCode, shipping, other, roundOff]);
  const setItem = (i, patch) => setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const pickProduct = (i, pid) => {
    if (priceListId && (priceRatesLoading || priceRatesError)) {
      toast.error(priceRatesError || "Wait for the selected price list rates to load, or deselect the price list.");
      return;
    }
    const p = products.find((x) => x.id === pid); if (!p) return;
    const plRate = plRates[pid];
    setItem(i, { product_id: p.id, name: p.name, hsn: p.hsn, batch_no: p.batch_no, expiry_date: p.expiry_date, unit: p.unit, rate: plRate != null ? plRate : (p.ptr || p.selling_price || p.mrp), mrp: p.mrp, gst_rate: p.gst_rate });
  };

  const save = async () => {
    if (priceListId && (priceRatesLoading || priceRatesError)) {
      return toast.error(priceRatesError || "Wait for selected price list rates to load, or deselect the price list.");
    }
    if (!editing && settingsLoading) return toast.error("Wait for company settings to load before saving.");
    if (!editing && settingsError) return toast.error(settingsError);
    if (!customerId) return toast.error("Select a customer");
    const valid = items.filter((it) => it.name && it.qty > 0);
    if (!valid.length) return toast.error("Add at least one product");
    setSaving(true);
    try {
      const payload = {
        customer_id: customerId, doc_date: docDate, valid_until: validUntil,
        place_of_supply: posName, place_of_supply_code: posCode, items: valid,
        shipping: +shipping, other_charges: +other, round_off_enabled: roundOff,
        transporter, vehicle_no: vehicleNo, lr_no: lrNo, notes,
        ...(!editing || (document && bankId !== originalBankSelection)
          ? { bank_id: bankId === "__none__" || bankId === "__saved_none__" || bankId === "__saved__" ? null : bankId }
          : {}),
      };
      let res;
      if (editing) res = await http.put(`/sales-docs/${cfg.api}/${id}`, payload);
      else res = await http.post(`/sales-docs/${cfg.api}`, payload);
      toast.success(`${cfg.label} saved`);
      navigate(`${cfg.base}/${res.data.id}`);
    } catch (e) { toast.error(requestErrorMessage(e, `save ${cfg.label.toLowerCase()}`)); }
    finally { setSaving(false); }
  };

  const documentText = resolveDocumentText(company, document, dtype);
  const banks = company.banks || [];
  const savedBankId = document?.bank_id != null && document.bank_id !== "" ? String(document.bank_id) : null;
  const showSavedBank = editing && document && (
    bankId === "__saved__" ||
    (savedBankId && !banks.some((bank) => String(bank.id) === savedBankId))
  );
  const savedBankOptionValue = bankId === "__saved__" ? "__saved__" : savedBankId;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-7xl">
      <button onClick={() => navigate(cfg.base)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back</button>
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{editing ? "Edit" : "New"} {cfg.label}</h1>
      {(prereqError || settingsError || documentError) && <div className="space-y-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
        {prereqError && <div className="flex items-center justify-between gap-3"><span>{prereqError}</span><Button size="sm" variant="outline" onClick={loadPrerequisites}>Retry prerequisite data</Button></div>}
        {settingsError && <div className="flex items-center justify-between gap-3"><span>{settingsError}</span><Button size="sm" variant="outline" onClick={loadSettings}>Retry company settings</Button></div>}
        {documentError && <div className="flex items-center justify-between gap-3"><span>{documentError}</span><Button size="sm" variant="outline" onClick={loadDocument}>Retry document</Button></div>}
      </div>}
      {prereqLoading && <div className="text-sm text-muted-foreground">Loading customers and products…</div>}
      {settingsLoading && <div className="text-sm text-muted-foreground">Loading company settings…</div>}
      {documentLoading && editing && <div className="text-sm text-muted-foreground">Loading document…</div>}

      <Card className="p-5 sm:p-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-2">
            <Label htmlFor="sdoc-customer-select">Customer *</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger id="sdoc-customer-select" className="mt-1.5 h-11" data-testid="sdoc-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
              <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="sdoc-pos-select">Place of Supply</Label>
            <Select value={posCode} onValueChange={(v) => { setPosCode(v); setPosName(STATES.find((s) => s[0] === v)?.[1] || ""); }}>
              <SelectTrigger id="sdoc-pos-select" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
              <SelectContent>{STATES.map((s) => <SelectItem key={s[0]} value={s[0]}>{s[0]} — {s[1]}</SelectItem>)}</SelectContent>
            </Select>
            <div className="text-xs mt-1.5 text-muted-foreground">{t.intra ? "Intra · CGST+SGST" : "Inter · IGST"}</div>
          </div>
          <div className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-2">
            <div><Label htmlFor="sdoc-date">Date</Label><Input id="sdoc-date" type="date" value={docDate} onChange={(e) => setDocDate(e.target.value)} className="mt-1.5 h-11 w-full" /></div>
            {cfg.dateLabel && <div><Label htmlFor="sdoc-valid-until">{cfg.dateLabel}</Label><Input id="sdoc-valid-until" type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className="mt-1.5 h-11 w-full" /></div>}
          </div>
          <div className="lg:col-span-2">
            <Label htmlFor="sdoc-bank-select">Bank for this document</Label>
            <Select value={bankId} onValueChange={setBankId}>
              <SelectTrigger id="sdoc-bank-select" className="mt-1.5 h-11" data-testid="sdoc-bank-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {showSavedBank && <SelectItem value={savedBankOptionValue}>{savedBankLabel(document.bank_details)}</SelectItem>}
                {editing && originalBankSelection === "__saved_none__" && <SelectItem value="__saved_none__">No bank (unchanged)</SelectItem>}
                <SelectItem value="__none__">No bank</SelectItem>
                {banks.map((bank, index) => <SelectItem key={bank.id || index} value={String(bank.id)}>{bank.name || bank.bank_name || "Bank"}{bank.account_number || bank.bank_account ? ` · ${bank.account_number || bank.bank_account}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        {dtype === "delivery_challan" && (
          <div className="grid sm:grid-cols-3 gap-4 mt-4">
            <div><Label>Transporter</Label><Input value={transporter} onChange={(e) => setTransporter(e.target.value)} className="mt-1.5" /></div>
            <div><Label>Vehicle No</Label><Input value={vehicleNo} onChange={(e) => setVehicleNo(e.target.value)} className="mt-1.5" /></div>
            <div><Label>LR No</Label><Input value={lrNo} onChange={(e) => setLrNo(e.target.value)} className="mt-1.5" /></div>
          </div>
        )}
      </Card>

      <Card className="p-0 overflow-hidden">
          <div className="p-3 sm:p-4 border-b flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-heading font-semibold">Items</h3>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            {priceLists.length > 0 && (
              <Select value={priceListId || "__none"} onValueChange={(v) => setPriceListId(v === "__none" ? "" : v)}>
                <SelectTrigger className="h-11 w-[min(44vw,10rem)] sm:h-9 sm:w-40" data-testid="sdoc-pricelist-select"><SelectValue placeholder="Price List" /></SelectTrigger>
                <SelectContent><SelectItem value="__none">Default price</SelectItem>{priceLists.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {priceListId && priceRatesError && <div className="flex w-full items-center justify-between gap-2 rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700">
              <span>{priceRatesError} Deselect the price list to use default rates.</span>
              <Button size="sm" variant="outline" onClick={() => setPriceRatesRetry((n) => n + 1)}>Retry</Button>
            </div>}
            {priceListId && priceRatesLoading && <span className="text-xs text-muted-foreground">Loading price list rates…</span>}
            {itc.Gear}{itc.FieldsButton}
            <Button size="sm" variant="outline" onClick={() => setItems((a) => [...a, newItem()])} className="min-h-11 sm:min-h-9" data-testid="sdoc-add-item"><Plus className="h-4 w-4 mr-1" /> Add Item</Button>
          </div>
        </div>
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50"><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-2 font-medium min-w-[200px]">Product</th>
              {itc.show("hsn") && <th className="px-2 py-2">HSN</th>}
              {itc.show("batch_no") && <th className="px-2 py-2">Batch</th>}
              {itc.customHeaders()}
              {itc.show("qty") && <th className="px-2 py-2 text-right">Qty</th>}
              {itc.show("rate") && <th className="px-2 py-2 text-right">Rate</th>}
              {itc.show("discount_pct") && <th className="px-2 py-2 text-right">Disc%</th>}
              {itc.show("gst_rate") && <th className="px-2 py-2 text-right">GST%</th>}
              <th className="px-2 py-2 text-right">Amount</th><th></th>
            </tr></thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} className="border-t">
                  <td className="px-3 py-2">
                    <Select value={it.product_id} disabled={Boolean(priceListId && (priceRatesLoading || priceRatesError))} onValueChange={(v) => pickProduct(i, v)}>
                      <SelectTrigger className="h-9" data-testid={`sdoc-product-${i}`}><SelectValue placeholder="Select product" /></SelectTrigger>
                      <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.batch_no}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  {itc.show("hsn") && <td className="px-2 py-2"><Input value={it.hsn} onChange={(e) => setItem(i, { hsn: e.target.value })} className="h-9 w-20 font-mono text-xs" /></td>}
                  {itc.show("batch_no") && <td className="px-2 py-2"><Input value={it.batch_no} onChange={(e) => setItem(i, { batch_no: e.target.value })} className="h-9 w-20 font-mono text-xs" /></td>}
                  {itc.customCells(it, (patch) => setItem(i, patch))}
                  {itc.show("qty") && <td className="px-2 py-2"><Input type="number" value={it.qty} onChange={(e) => setItem(i, { qty: +e.target.value })} className="h-9 w-16 text-right" data-testid={`sdoc-qty-${i}`} /></td>}
                  {itc.show("rate") && <td className="px-2 py-2"><Input type="number" value={it.rate} onChange={(e) => setItem(i, { rate: +e.target.value })} className="h-9 w-20 text-right" /></td>}
                  {itc.show("discount_pct") && <td className="px-2 py-2"><Input type="number" value={it.discount_pct} onChange={(e) => setItem(i, { discount_pct: +e.target.value })} className="h-9 w-16 text-right" /></td>}
                  {itc.show("gst_rate") && <td className="px-2 py-2"><Input type="number" value={it.gst_rate} onChange={(e) => setItem(i, { gst_rate: +e.target.value })} className="h-9 w-16 text-right" /></td>}
                  <td className="px-2 py-2 text-right font-mono">{inr(t.rows[i]?.amount || 0)}</td>
                  <td className="px-2 py-2"><button onClick={() => setItems((a) => a.length > 1 ? a.filter((_, idx) => idx !== i) : a)} className="p-1.5 hover:bg-rose-50 rounded"><Trash2 className="h-4 w-4 text-rose-500" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="space-y-3 p-3 md:hidden">
          {items.map((it, i) => (
            <section key={i} className="rounded-lg border border-border bg-card p-3 space-y-4" data-testid={`sdoc-item-card-${i}`}>
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <Label htmlFor={`sdoc-product-mobile-${i}`}>Product</Label>
                  <Select value={it.product_id} disabled={Boolean(priceListId && (priceRatesLoading || priceRatesError))} onValueChange={(v) => pickProduct(i, v)}>
                    <SelectTrigger id={`sdoc-product-mobile-${i}`} className="mt-1 h-11" data-testid={`sdoc-product-mobile-${i}`}><SelectValue placeholder="Select product" /></SelectTrigger>
                    <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.batch_no}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <button type="button" onClick={() => setItems((a) => a.length > 1 ? a.filter((_, idx) => idx !== i) : a)} aria-label={`Remove item ${i + 1}`} className="mt-6 grid h-11 w-11 shrink-0 place-items-center rounded-md text-rose-600 hover:bg-rose-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {itc.show("hsn") && <div><Label htmlFor={`sdoc-hsn-${i}`} className="text-xs text-muted-foreground">HSN</Label><Input id={`sdoc-hsn-${i}`} aria-label={`HSN for item ${i + 1}`} value={it.hsn || ""} onChange={(e) => setItem(i, { hsn: e.target.value })} className="mt-1 h-11 w-full font-mono text-sm" /></div>}
                {itc.show("batch_no") && <div><Label htmlFor={`sdoc-batch-${i}`} className="text-xs text-muted-foreground">Batch</Label><Input id={`sdoc-batch-${i}`} aria-label={`Batch for item ${i + 1}`} value={it.batch_no || ""} onChange={(e) => setItem(i, { batch_no: e.target.value })} className="mt-1 h-11 w-full font-mono text-sm" /></div>}
                {itc.show("qty") && <div><Label htmlFor={`sdoc-qty-mobile-${i}`} className="text-xs text-muted-foreground">Qty</Label><Input id={`sdoc-qty-mobile-${i}`} type="number" value={it.qty} onChange={(e) => setItem(i, { qty: +e.target.value })} className="mt-1 h-11 w-full text-right" data-testid={`sdoc-qty-${i}`} /></div>}
                {itc.show("rate") && <div><Label htmlFor={`sdoc-rate-${i}`} className="text-xs text-muted-foreground">Rate</Label><Input id={`sdoc-rate-${i}`} type="number" value={it.rate} onChange={(e) => setItem(i, { rate: +e.target.value })} className="mt-1 h-11 w-full text-right" /></div>}
                {itc.show("discount_pct") && <div><Label htmlFor={`sdoc-discount-${i}`} className="text-xs text-muted-foreground">Discount %</Label><Input id={`sdoc-discount-${i}`} type="number" value={it.discount_pct} onChange={(e) => setItem(i, { discount_pct: +e.target.value })} className="mt-1 h-11 w-full text-right" /></div>}
                {itc.show("gst_rate") && <div><Label htmlFor={`sdoc-gst-${i}`} className="text-xs text-muted-foreground">GST %</Label><Input id={`sdoc-gst-${i}`} type="number" value={it.gst_rate} onChange={(e) => setItem(i, { gst_rate: +e.target.value })} className="mt-1 h-11 w-full text-right" /></div>}
                {itc.customMobileFields(it, (patch) => setItem(i, patch), i)}
              </div>
              <div className="flex items-center justify-between border-t pt-3 text-sm">
                <span className="text-muted-foreground">Line amount</span><span className="font-mono font-semibold">{inr(t.rows[i]?.amount || 0)}</span>
              </div>
            </section>
          ))}
        </div>
      </Card>
      {itc.Modal}

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5 space-y-4">
          <div className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-4">
            <div><Label htmlFor="sdoc-shipping">Shipping</Label><Input id="sdoc-shipping" type="number" value={shipping} onChange={(e) => setShipping(+e.target.value)} className="mt-1.5 h-11" /></div>
            <div><Label htmlFor="sdoc-other">Other Charges</Label><Input id="sdoc-other" type="number" value={other} onChange={(e) => setOther(+e.target.value)} className="mt-1.5 h-11" /></div>
          </div>
          <div className="flex items-center gap-3"><Switch checked={roundOff} onCheckedChange={setRoundOff} /><Label>Auto Round-Off</Label></div>
          <div><Label htmlFor="sdoc-notes">Notes</Label><Input id="sdoc-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5 h-11" /></div>
          {(documentText.notes || documentText.terms) && <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-2">
            <div className="flex justify-between items-center"><span className="font-medium">Shared document text</span><Link to="/settings" className="text-xs text-brand-600 hover:underline">Settings</Link></div>
            {documentText.notes && <div><div className="text-xs font-medium text-muted-foreground">Notes</div><p className="whitespace-pre-wrap">{documentText.notes}</p></div>}
            {documentText.terms && <div><div className="text-xs font-medium text-muted-foreground">Terms &amp; Conditions</div><p className="whitespace-pre-wrap">{documentText.terms}</p></div>}
          </div>}
        </Card>
        <Card className="p-5">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Taxable</span><span className="font-mono">{inr(t.subtotal)}</span></div>
            {t.intra ? (<><div className="flex justify-between"><span className="text-muted-foreground">CGST</span><span className="font-mono">{inr(t.cgst)}</span></div><div className="flex justify-between"><span className="text-muted-foreground">SGST</span><span className="font-mono">{inr(t.sgst)}</span></div></>) : <div className="flex justify-between"><span className="text-muted-foreground">IGST</span><span className="font-mono">{inr(t.igst)}</span></div>}
            <div className="flex justify-between"><span className="text-muted-foreground">Round Off</span><span className="font-mono">{inr(t.roundOff)}</span></div>
            <div className="flex justify-between pt-2 border-t font-heading text-lg font-bold"><span>Grand Total</span><span className="tabular" data-testid="sdoc-grand-total">{inr(t.grand)}</span></div>
          </div>
        </Card>
      </div>

      <div className="flex sticky bottom-0 z-10 bg-background/95 backdrop-blur py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:justify-end">
        <Button className="min-h-11 w-full sm:w-auto" onClick={save} disabled={saving || prereqLoading || !!prereqError || Boolean(priceListId && (priceRatesLoading || priceRatesError)) || (!editing && (settingsLoading || !!settingsError)) || (editing && (!document || documentLoading || !!documentError))} data-testid="save-sdoc-button"><Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : `Save ${cfg.label}`}</Button>
      </div>
    </div>
  );
}
